"use client";

import { useState, useEffect } from "react";
import { Search, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";

export function InteractiveTeaser() {
  const t = useTranslations("landing.interactiveTeaser");
  const query = t("query");
  const answer = t("answer");

  const [text, setText] = useState("");
  const [showAnswer, setShowAnswer] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const startTyping = () => {
      setIsTyping(true);
      setText("");
      setShowAnswer(false);

      let i = 0;
      const typeInterval = setInterval(() => {
        setText(query.slice(0, i + 1));
        i++;
        if (i === query.length) {
          clearInterval(typeInterval);
          setIsTyping(false);
          timeout = setTimeout(() => {
            setShowAnswer(true);
            // Reset after showing answer for a while
            timeout = setTimeout(startTyping, 8000);
          }, 600);
        }
      }, 50);
    };

    // Initial start delay
    timeout = setTimeout(startTyping, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [query]);

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 py-24 text-white">
      <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
      <div className="relative z-10 container mx-auto px-4">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <h2 className="mb-4 text-3xl font-bold">{t("title")}</h2>
          <p className="text-lg text-gray-400">{t("description")}</p>
        </div>

        <div className="mx-auto max-w-2xl">
          {/* Search Bar */}
          <div className="mb-8 flex transform items-center gap-3 rounded-xl bg-white p-2 shadow-2xl transition-all hover:scale-[1.02]">
            <div className="rounded-lg bg-gray-100 p-2">
              <Search className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
            <div className="flex h-6 flex-1 items-center font-medium text-gray-900">
              {text}
              {isTyping && (
                <span className="ml-0.5 h-5 w-0.5 animate-pulse bg-blue-500" />
              )}
            </div>
          </div>

          {/* AI Answer */}
          <div className="h-auto min-h-[12rem]">
            {" "}
            {/* Fixed height to prevent layout shift */}
            <AnimatePresence>
              {showAnswer && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="relative rounded-xl border border-gray-700 bg-gray-800/50 p-6 backdrop-blur-sm"
                >
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold tracking-wide text-teal-400 uppercase">
                    <Sparkles className="h-4 w-4" />
                    {t("assistantLabel")}
                  </div>
                  <p className="text-lg leading-relaxed text-gray-200">
                    {answer}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                    {t("source")}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
